import { Magnification } from "@/app/activity/components/timeline/activityTimeline/type"
import { Range } from "@/app/activity/components/timeline/activityTimeline/type"
import { Activity, ActivityData } from "@/types/activity"
import { ActivityTypeKey, MatchDayType } from "@/types/activity"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { PlayerRemoveDialogProps, SetSessionGuideLine } from "@/app/activity/store/type"

export type ActivityViewMode = "month" | "week" | "schedule"

export type ActivityFilters = {
  types: ActivityTypeKey[]
  seasonWeekIds: number[]
  matchDays: MatchDayType[]
  playerIds: number[]
  query: string
  startOnMonday: boolean
  alignToMatchDay: boolean
}

export type ActivityCommonState = {
  loading: boolean
  selectedDate: Date
  currentDate: Date
  translateX: number
  timelineWidth: number
  timePerWidth: number
  range: Range
  magnification: Magnification
  tempRange?: Range
  remainingArea?: number
  sessionMouseGuideLine?: SetSessionGuideLine
  activityDataForMatch?: ActivityData
  playerRemoveDialogProps?: PlayerRemoveDialogProps
  viewMode: ActivityViewMode
  filters: ActivityFilters
  sortKey: "uniformNumber" | "nickname" | "position"
  sortDirection: "asc" | "desc"
  infoDialogActivity: Pick<Activity, "idfActivity" | "name"> | null
}

export const initialActivityFilters: ActivityFilters = {
  types: [],
  seasonWeekIds: [],
  matchDays: [],
  playerIds: [],
  query: "",
  startOnMonday: false,
  alignToMatchDay: false,
}

export const initialActivityCommonState: ActivityCommonState = {
  loading: false,
  selectedDate: new Date(),
  currentDate: new Date(),
  translateX: 0,
  timelineWidth: 0,
  timePerWidth: 0,
  range: {} as Range,
  tempRange: undefined,
  magnification: Magnification.INIT,
  remainingArea: 0,
  sessionMouseGuideLine: undefined,
  activityDataForMatch: undefined,
  playerRemoveDialogProps: undefined,
  viewMode: "month",
  filters: initialActivityFilters,
  sortKey: "uniformNumber",
  sortDirection: "asc",
  infoDialogActivity: null,
}

export type ActivityCommonStateActions = {
  setLoading: (loading: boolean) => void
  setSelectedDate: (selectedDate: Date) => void
  setCurrentDate: (currentDate: Date) => void
  setTranslateX: (translateX: number) => void
  setTimelineWidth: (timelineWidth: number) => void
  setTimePerWidth: (timePerWidth: number) => void
  setRange: (range: Range) => void
  setTempRange: (tempRange: Range) => void
  setMagnification: (magnification: number) => void
  setRemainingArea: (remainingArea?: number) => void
  setSessionMouseGuideLine: (sessionGuideLine?: SetSessionGuideLine) => void
  setIdfDataForMatch: (activityDataForMatch?: ActivityData) => void
  setPlayerRemoveDialogProps: (playerRemoveDialogProps?: PlayerRemoveDialogProps) => void
  setViewMode: (viewMode: ActivityViewMode) => void
  setFilters: (filters: Partial<ActivityFilters>) => void
  resetFilters: () => void
  setSortKey: (key: "uniformNumber" | "nickname" | "position") => void
  setSortDirection: (direction: "asc" | "desc") => void
  setInfoDialogActivity: (infoDialogActivity: Pick<Activity, "idfActivity" | "name"> | null) => void
}

export type ActivityCommonStore = ActivityCommonState & ActivityCommonStateActions

export const createActivityCommonStore = create<ActivityCommonStore>()(
  devtools(
    (set) => ({
      ...initialActivityCommonState,
      setLoading: (loading: boolean) => {
        set({ loading }, undefined, "setLoading")
      },
      setSelectedDate: (selectedDate: Date) => {
        set({ selectedDate }, undefined, "setSelectedDate")
      },
      setCurrentDate: (currentDate: Date) => {
        set({ currentDate }, undefined, "setCurrentDate")
      },
      setTranslateX: (translateX: number) => {
        set({ translateX }, undefined, "setTranslateX")
      },
      setTimelineWidth: (timelineWidth: number) => {
        set({ timelineWidth }, undefined, "setTimelineWidth")
      },
      setTimePerWidth: (timePerWidth: number) => {
        set({ timePerWidth }, undefined, "setTimePerWidth")
      },
      setRange: (range: Range) => {
        set({ range }, undefined, "setRange")
      },
      setTempRange: (tempRange: Range) => {
        set({ tempRange }, undefined, "setTempRange")
      },
      setMagnification: (magnification: Magnification) => {
        set({ magnification }, undefined, "setMagnification")
      },
      setRemainingArea: (remainingArea?: number) => {
        set({ remainingArea }, undefined, "setRemainingArea")
      },
      setSessionMouseGuideLine: (setSessionMouseGuideLine?: SetSessionGuideLine) => {
        set(
          { sessionMouseGuideLine: setSessionMouseGuideLine },
          undefined,
          "setSessionMouseGuideLine",
        )
      },
      setIdfDataForMatch: (activityDataForMatch?: ActivityData) => {
        set({ activityDataForMatch }, undefined, "setIdfDataForMatch")
      },
      setPlayerRemoveDialogProps: (playerRemoveDialogProps?: PlayerRemoveDialogProps) => {
        set({ playerRemoveDialogProps }, undefined, "setPlayerRemoveDialogProps")
      },
      setViewMode: (viewMode: ActivityViewMode) => {
        set({ viewMode }, undefined, "setViewMode")
      },
      setFilters: (filters: Partial<ActivityFilters>) => {
        set((state) => ({ filters: { ...state.filters, ...filters } }), undefined, "setFilters")
      },
      resetFilters: () => {
        set({ filters: initialActivityFilters }, undefined, "resetFilters")
      },
      setSortKey: (sortKey: "uniformNumber" | "nickname" | "position") => {
        set({ sortKey }, undefined, "setSortKey")
      },
      setSortDirection: (sortDirection: "asc" | "desc") => {
        set({ sortDirection }, undefined, "setSortDirection")
      },
      setInfoDialogActivity: (
        infoDialogActivity: Pick<Activity, "idfActivity" | "name"> | null,
      ) => {
        set({ infoDialogActivity }, undefined, "setInfoDialogActivity")
      },
    }),
    {
      store: "activityCommon",
      enabled: process.env.NODE_ENV !== "production",
    },
  ),
)
